import { roles, roleLevels, rolesWhichAllowFilters } from '../resources/roles';
import {
  licenses,
  matchOnIdAccessExclusions,
  restrictedOperationalReportAccess,
} from '../resources/districtLevelReportAccess';

import { reportIdentifiers as mapGrowthReports } from '../components/GrowthReports/reportDetails';
import { reportIdentifiers as operationalReports } from '../components/OperationalReports/reportDetails';

const licenseTypes = Object.keys(licenses);

/* getReportsForUser
 * parameters: userRoles - string array of roles returned by the user profile service
 *             userLicenses - string array of license types returned by the getUserLicenses endpoint
 * returns: object containing arrays of reports for Map Growth and Operational Reports based on
 *          roles and licenses assigned to the user
 * An empty array indicates that there is no access for that category.
 *
 * Report access is based on the highest access given for a role when multiple roles are present.
 *
 * User access is based on the presence of the report type and exclusions to the entire report list.
 * For example:
 *
 *     "ASSESSMENT_COORDINATOR": {
 *         "mapGrowthReports": {},
 *         "operationalReports": {
 *             "excludeReports": "studentsByDistrict"
 *         }
 *     }
 *
 * District Assessment Coordinator has access to all Map Growth reports and Operational Reports except Students By
 * District report.  If the DAC role had no access to a role in either category, it would not be included in the json
 * object.  So, if DAC had no access to Growth Reports, "mapGrowthReports" would be absent from the object.
 *
 * Licensing:  At the time of this writing, we only check for a PG1 license that gives access to Map Growth MPG (screening
 *             and checklist reports).  If the district does not have that license assigned (and therefore the user does
 *             not have that license assigned), access to the MPG Map Growth reports will be removed from the above
 *             example before returning the access object.
 *
 * Special Access:  There are several one-off district-level configurations that we need to take into account after
 *                  we compute the normal report access by role.
 *                  1.  assessment.system_configuration importProfile.matchOnId = TRUE, this configuration will remove
 *                      access to the Potential Duplicate Profile operational report for everyone in the district.
 *                  2.  assessment.system_configuration restricted.operational.report.access = TRUE, this configuration
 *                      will remove SWoVTR and Test Events By Status from the operational report list for all roles.
 *                      This will be set up for Chicago only in the database.
 *                  3.  assessment.system_configuration additional.operational.report.access = "ADMINISTRATOR", this
 *                      configuration will let all ADMINISTRATORS see the SWOVTR and Test Events By Status reports.
 *
 */
function getReportsForUser(userRoles, userLicenses, accessExceptions) {
  let operationalReportAccess = [];
  let mapGrowthReportAccess = [];

  //Determine access based on the user's roles
  if (Array.isArray(userRoles)) {
    userRoles.forEach((role) => {
      //If the role exists in our matrix for report access, get a set of reports that user can access.
      if (roles[role] !== undefined) {
        if (roles[role].mapGrowthReports !== undefined) {
          let excludeReportSet =
            roles[role].mapGrowthReports.excludeReports !== undefined
              ? toArray(roles[role].mapGrowthReports.excludeReports)
              : [];
          let roleAccess = difference(mapGrowthReports, excludeReportSet);
          //The following is a way of getting a union that does not duplicate entries
          mapGrowthReportAccess = [...new Set([...mapGrowthReportAccess, ...roleAccess])];
        }
        if (roles[role].operationalReports !== undefined) {
          let excludeReportSet =
            roles[role].operationalReports.excludeReports !== undefined
              ? toArray(roles[role].operationalReports.excludeReports)
              : [];
          let roleAccess = difference(operationalReports, excludeReportSet);
          operationalReportAccess = [...new Set([...operationalReportAccess, ...roleAccess])];
        }
      }
    });
  }

  //Ok, big breath.  Ready?

  //Apply licensing restrictions, if necessary
  //If the user has a license that corresponds to licensed reports, don't remove them from the access.
  //At this point in time, we don't restrict operational reports.  If this changes, we'll need to change
  // this logic (and perhaps how the license json file is structured) a bit to include them.
  licenseTypes.forEach((licenseType) => {
    const reportLicenseRestrictions =
      userLicenses && userLicenses.includes(licenseType) ? [] : toArray(licenses[licenseType]);
    mapGrowthReportAccess = difference(mapGrowthReportAccess, reportLicenseRestrictions);
  });

  //Apply any other applicable district-level inclusions/exclusions

  //The one partner that wants SWOVTR and Test Events By Status removed from all roles.
  const operationalReportExclusions =
    accessExceptions['restricted.operational.report.access'] &&
    accessExceptions['restricted.operational.report.access'].toUpperCase() === 'TRUE'
      ? toArray(restrictedOperationalReportAccess)
      : [];
  operationalReportAccess = difference(operationalReportAccess, operationalReportExclusions);

  //MatchOnId removes Potential Duplicate Profiles report

  const reportMatchOnIdRestrictions =
    accessExceptions['importProfile.matchOnId'] && accessExceptions['importProfile.matchOnId'].toUpperCase() === 'TRUE'
      ? toArray(matchOnIdAccessExclusions)
      : [];
  operationalReportAccess = difference(operationalReportAccess, reportMatchOnIdRestrictions);

  // Now, for our final act, we need to figure out if we have an additional.operational.report access setting
  // that allows that role in a district to see extra operational reports.  Whew.
  if (userRoles && Array.isArray(userRoles) && accessExceptions['additional.operational.report.access'] !== '') {
    const additionalAccessRoles = toArray(accessExceptions['additional.operational.report.access']);
    additionalAccessRoles.forEach((role) => {
      if (userRoles.includes(role)) {
        const additionalReportAccessList = toArray(roles[role].additionalReportAccess.includeOpsReports);
        //Now that we can disable reports, we don't need a add extra access to a report that is disabled.
        let enabledAccessList = [];
        additionalReportAccessList.forEach((report) => {
          if (operationalReports.includes(report)) {
            enabledAccessList.push(report);
          }
        });
        operationalReportAccess = [...new Set([...operationalReportAccess, ...enabledAccessList])];
      }
    });
  }

  return { mapGrowthReports: mapGrowthReportAccess, operationalReports: operationalReportAccess };
}

function toArray(rolesObjectData) {
  return rolesObjectData ? rolesObjectData.replace(/\s/g, '').split(',') : [];
}

export function difference(arr1, arr2) {
  return arr1.filter((x) => !arr2.includes(x));
}

function getUserRoleLevel(userRoles) {
  let userLevel = roleLevels.none;
  if (userRoles) {
    rolesWhichAllowFilters.every((roleInfo) => {
      if (roleInfo.roles.some((role) => userRoles.includes(role))) {
        userLevel = roleInfo.level;
      }
      return userLevel === roleLevels.none;
    });
  }

  return userLevel;
}

export default {
  getReportsForUser,
  getUserRoleLevel,
  difference,
};
